import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Intent, Tag } from '@blueprintjs/core';

import { ShowHide } from 'components/layout';

import { formatValue } from './diffViewerFormatting';

const notAvailableText = "—";
const notMeasuredText = "not measured in UnderTest";
const coverageUnavailableText = "coverage unavailable";
const gateFailedText = "could not be loaded";

function formatCount(value) {
    if (value == null) {
        return notAvailableText;
    }

    return formatValue(value);
}

function formatStillOpen(gate, gateFailed) {
    if (gateFailed) {
        return gateFailedText;
    }

    return formatCount(gate?.stillOpen);
}

function hasDriverFigures(gate) {
    return gate != null
        && gate.analysedDrivers != null
        && gate.notAnalysedEnvironmental != null
        && gate.notAnalysedGenuine != null
        && gate.waiting != null;
}


/**
 * The exit-gate figure, read only from the gate the endpoint reports and never from the differences on
 * screen, so no filter or toggle on the page can move it. Coverage for a chosen period has its own line.
 */
export function DiffViewerGatePanel(
    {
        gate = null,
        gateFailed = false,
        coverage = null,
        coverageFailed = false,
        coverageMeasured = true,
        periodChosen = false,
        loading = false
    }
) {
    return (
        <div id="rules-engine-diff-gate" className={classNames("spacer-bottom", { "bp6-skeleton": loading })}>
            <h3 id="rules-engine-diff-gate-heading">Exit gate</h3>

            <p id="rules-engine-diff-gate-still-open" className="inline-items">
                <span>{`Still open: ${formatStillOpen(gate, gateFailed)}`}</span>
                <ShowHide
                    evaluator={!gateFailed && gate?.stillOpenProvisional != null}
                    show={(
                        <span id="rules-engine-diff-gate-provisional">{`of which ${formatCount(gate?.stillOpenProvisional)} provisional`}</span>
                    )}
                />
            </p>

            <ShowHide
                evaluator={!gateFailed && gate != null}
                show={(
                    <p id="rules-engine-diff-gate-coverage" className="inline-items">
                        <ShowHide
                            evaluator={hasDriverFigures(gate)}
                            show={(
                                <span className="inline-items">
                                    <span>{`Analysed ${formatCount(gate?.analysedDrivers)}`}</span>
                                    <span>{`· not analysed ${formatCount(gate?.notAnalysedEnvironmental)} environmental /`}</span>
                                    <Tag id="rules-engine-diff-gate-genuine" intent={Intent.WARNING} minimal>
                                        {`${formatCount(gate?.notAnalysedGenuine)} genuine`}
                                    </Tag>
                                    <span>{`· still waiting ${formatCount(gate?.waiting)}`}</span>
                                </span>
                            )}
                            hide={(
                                <span id="rules-engine-diff-gate-not-measured">{`Drivers ${notMeasuredText}`}</span>
                            )}
                        />
                    </p>
                )}
            />

            <ShowHide
                evaluator={periodChosen}
                show={(
                    <div id="rules-engine-diff-period">
                        <h4 id="rules-engine-diff-period-heading">Selected period</h4>
                        <ShowHide
                            evaluator={coverageMeasured}
                            show={(
                                <ShowHide
                                    evaluator={coverage != null && !coverageFailed}
                                    show={(
                                        <p id="rules-engine-diff-period-coverage" className="inline-items">
                                            <span>{`Attempted ${formatCount(coverage?.attempted)}`}</span>
                                            <span>{`· analysed ${formatCount(coverage?.analysed)}`}</span>
                                            <span>{`· with differences ${formatCount(coverage?.analysedWithDifferences)}`}</span>
                                            <span>{`· not analysed ${formatCount(coverage?.notAnalysedEnvironmental)} environmental /`}</span>
                                            <Tag id="rules-engine-diff-period-genuine" intent={Intent.WARNING} minimal>
                                                {`${formatCount(coverage?.notAnalysedGenuine)} genuine`}
                                            </Tag>
                                            <span>{`· still waiting ${formatCount(coverage?.waiting)}`}</span>
                                        </p>
                                    )}
                                    hide={(
                                        <p id="rules-engine-diff-period-coverage">{coverageFailed ? coverageUnavailableText : notAvailableText}</p>
                                    )}
                                />
                            )}
                            hide={(
                                <p id="rules-engine-diff-period-coverage">{`Drivers ${notMeasuredText}`}</p>
                            )}
                        />
                    </div>
                )}
            />
        </div>
    );
}

DiffViewerGatePanel.propTypes = {
    gate: PropTypes.object,
    gateFailed: PropTypes.bool,
    coverage: PropTypes.object,
    coverageFailed: PropTypes.bool,
    coverageMeasured: PropTypes.bool,
    periodChosen: PropTypes.bool,
    loading: PropTypes.bool
};
